import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Search, Download, Plus, Pencil, Trash2, Home as HomeIcon, Users, Building2, Lock,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import {
  login, logout, isAuthed, getRegistrations, getUnits, saveUnit, deleteUnit,
} from "../api";
import { UNIT_TYPES, STATUS_OPTIONS } from "../mock";

const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const statusStyle = {
  "متاح": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "محجوز": "bg-amber-50 text-amber-700 border-amber-200",
  "مباع": "bg-rose-50 text-rose-700 border-rose-200",
};

const Login = ({ onDone }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try { await login(u, p); onDone(); }
    catch (e) { setErr(e.message || "خطأ"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#241C15] px-5">
      <form onSubmit={submit} className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl">
        <div className="w-14 h-14 rounded-2xl bg-[#BF9A5A] flex items-center justify-center mx-auto mb-5">
          <Lock className="text-white" size={26} />
        </div>
        <h1 className="font-display text-2xl font-700 text-center text-[#241C15]">لوحة التحكم</h1>
        <p className="text-center text-stone-500 text-sm mt-1 mb-6">مسار كورنر</p>

        <Label className="text-stone-600 mb-1.5 block">اسم المستخدم</Label>
        <Input value={u} onChange={(e) => setU(e.target.value)} className="mb-4" />
        <Label className="text-stone-600 mb-1.5 block">كلمة المرور</Label>
        <Input type="password" value={p} onChange={(e) => setP(e.target.value)} />

        {err && <p className="text-rose-600 text-sm mt-3">{err}</p>}

        <button disabled={loading} className="w-full mt-6 py-3 rounded-xl bg-[#BF9A5A] hover:bg-[#a9853f] text-white font-700 transition-colors disabled:opacity-60">
          {loading ? "جارٍ الدخول..." : "دخول"}
        </button>
      </form>
    </div>
  );
};

const emptyUnit = { unitNumber: "", type: UNIT_TYPES[0], area: "", price: "", floor: "", status: "متاح", tower: "البرج الطويل" };

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [authed, setAuthed] = useState(isAuthed());
  const [regs, setRegs] = useState([]);
  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setRegs(await getRegistrations());
    setUnits(await getUnits());
  };
  useEffect(() => { if (authed) load(); }, [authed]);

  const filteredRegs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return regs;
    return regs.filter((r) =>
      [r.name, r.phone, r.unitNumber, r.budget, r.callTime]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [regs, search]);

  const exportExcel = () => {
    const rows = filteredRegs.map((r) => `
      <tr><td>${r.name || ""}</td><td>${r.phone || ""}</td><td>${r.unitNumber || ""}</td>
      <td>${r.budget || ""}</td><td>${r.callTime || ""}</td>
      <td>${new Date(r.createdAt).toLocaleString("ar-SA")}</td></tr>`).join("");
    const html = `<html dir="rtl"><head><meta charset="utf-8"></head><body>
      <table border="1"><thead><tr>
      <th>الاسم</th><th>الجوال</th><th>رقم الوحدة</th><th>الميزانية</th><th>وقت الاتصال</th><th>التاريخ</th>
      </tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `تسجيلات-الاهتمام-${new Date().toISOString().slice(0, 10)}.xls`;
    a.click();
  };

  const handleSaveUnit = async () => {
    const payload = { ...editing, area: Number(editing.area), price: Number(editing.price), floor: Number(editing.floor) };
    await saveUnit(payload);
    setUnits(await getUnits());
    setEditing(null);
    toast({ title: "تم الحفظ ✓" });
  };

  const handleDelete = async (id) => {
    await deleteUnit(id);
    setUnits(await getUnits());
    toast({ title: "تم الحذف" });
  };

  const doLogout = () => { logout(); setAuthed(false); };

  if (!authed) return <Login onDone={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#F3ECE0]">
      {/* top bar */}
      <header className="bg-[#241C15] text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-display text-xl font-700">لوحة التحكم</span>
            <span className="text-[#BF9A5A]">◈</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-stone-300 hover:text-white transition-colors">
              <HomeIcon size={16} /> الموقع
            </button>
            <button onClick={doLogout} className="flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors">
              <LogOut size={16} /> خروج
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <Tabs defaultValue="regs">
          <TabsList className="mb-6 bg-white border border-stone-200">
            <TabsTrigger value="regs" className="data-[state=active]:bg-[#BF9A5A] data-[state=active]:text-white gap-1.5">
              <Users size={16} /> تسجيلات الاهتمام ({regs.length})
            </TabsTrigger>
            <TabsTrigger value="units" className="data-[state=active]:bg-[#BF9A5A] data-[state=active]:text-white gap-1.5">
              <Building2 size={16} /> إدارة الوحدات ({units.length})
            </TabsTrigger>
          </TabsList>

          {/* Registrations */}
          <TabsContent value="regs">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم، الجوال، رقم الوحدة..." className="pr-10 bg-white" />
              </div>
              <button onClick={exportExcel} className="flex items-center gap-2 bg-[#241C15] hover:bg-black text-white px-4 py-2.5 rounded-xl font-700 text-sm transition-colors">
                <Download size={16} /> تصدير Excel
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
              <table className="w-full text-sm text-right">
                <thead className="bg-[#EFE6D6] text-stone-600">
                  <tr>
                    <th className="px-4 py-3 font-700">الاسم</th>
                    <th className="px-4 py-3 font-700">الجوال</th>
                    <th className="px-4 py-3 font-700">رقم الوحدة</th>
                    <th className="px-4 py-3 font-700">الميزانية</th>
                    <th className="px-4 py-3 font-700">وقت الاتصال</th>
                    <th className="px-4 py-3 font-700">التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegs.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-stone-400">لا توجد تسجيلات بعد.</td></tr>
                  ) : filteredRegs.map((r) => (
                    <tr key={r.id} className="border-t border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-3 font-500">{r.name || "—"}</td>
                      <td className="px-4 py-3" dir="ltr">{r.phone || "—"}</td>
                      <td className="px-4 py-3">{r.unitNumber || "—"}</td>
                      <td className="px-4 py-3">{r.budget || "—"}</td>
                      <td className="px-4 py-3">{r.callTime || "—"}</td>
                      <td className="px-4 py-3 text-stone-500">{new Date(r.createdAt).toLocaleString("ar-SA")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Units */}
          <TabsContent value="units">
            <div className="flex justify-end mb-4">
              <button onClick={() => setEditing({ ...emptyUnit })} className="flex items-center gap-2 bg-[#BF9A5A] hover:bg-[#a9853f] text-white px-4 py-2.5 rounded-xl font-700 text-sm transition-colors">
                <Plus size={16} /> إضافة وحدة
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
              <table className="w-full text-sm text-right">
                <thead className="bg-[#EFE6D6] text-stone-600">
                  <tr>
                    <th className="px-4 py-3 font-700">رقم الوحدة</th>
                    <th className="px-4 py-3 font-700">النوع</th>
                    <th className="px-4 py-3 font-700">المساحة</th>
                    <th className="px-4 py-3 font-700">الدور</th>
                    <th className="px-4 py-3 font-700">السعر</th>
                    <th className="px-4 py-3 font-700">الحالة</th>
                    <th className="px-4 py-3 font-700">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u) => (
                    <tr key={u.id} className="border-t border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-3 font-700">{u.unitNumber || "—"}</td>
                      <td className="px-4 py-3">{u.type}</td>
                      <td className="px-4 py-3">{u.area} م²</td>
                      <td className="px-4 py-3">{u.floor}</td>
                      <td className="px-4 py-3">{fmt(u.price)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-700 px-2.5 py-1 rounded-full border ${statusStyle[u.status] || ""}`}>{u.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => setEditing(u)} className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-600 transition-colors"><Pencil size={15} /></button>
                          <button onClick={() => handleDelete(u.id)} className="p-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 transition-colors"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Unit editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent dir="rtl">
          <DialogHeader className="text-right">
            <DialogTitle className="font-display text-xl">{editing?.id ? "تعديل الوحدة" : "إضافة وحدة"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="mb-1.5 block text-stone-600">رقم الوحدة</Label>
                <Input value={editing.unitNumber} onChange={(e) => setEditing({ ...editing, unitNumber: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label className="mb-1.5 block text-stone-600">نوع الوحدة</Label>
                <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{UNIT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-stone-600">المساحة (م²)</Label>
                <Input type="number" value={editing.area} onChange={(e) => setEditing({ ...editing, area: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-stone-600">الدور</Label>
                <Input type="number" value={editing.floor} onChange={(e) => setEditing({ ...editing, floor: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-stone-600">السعر (ريال)</Label>
                <Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              </div>
              <div>
                <Label className="mb-1.5 block text-stone-600">الحالة</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => setEditing(null)} className="px-4 py-2.5 rounded-xl border border-stone-200 text-stone-600 font-700">إلغاء</button>
            <button onClick={handleSaveUnit} className="px-5 py-2.5 rounded-xl bg-[#BF9A5A] hover:bg-[#a9853f] text-white font-700 transition-colors">حفظ</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
